import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import SignaturePad from './SignaturePad';
import signatureService from '../services/signature';

const SignatureManager = () => {
  const [signature, setSignature] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const fetchSignature = async () => {
      try {
        setLoading(true);
        const response = await signatureService.getUserSignature();
        if (response.exists) {
          setSignature(response.signature.imageData);
          setSaved(true);
        }
      } catch (error) {
        console.error('Failed to fetch signature:', error);
        toast.error('Failed to load your saved signature');
      } finally {
        setLoading(false);
      }
    };

    fetchSignature();
  }, []);

  const handleSaveSignature = async (signatureData) => {
    try {
      setLoading(true);
      await signatureService.saveSignature(signatureData);
      setSignature(signatureData);
      setSaved(true);
      toast.success('Signature saved successfully');
    } catch (error) {
      console.error('Failed to save signature:', error);
      // The signature service already extracts error.response.data, so we access error.error directly
      const errorMessage = error?.error || error?.message || 'Failed to save signature';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSignature = async () => {
    if (!saved) {
      setSignature(null);
      return;
    }

    if (window.confirm('Are you sure you want to delete your saved signature?')) {
      try {
        setLoading(true);
        await signatureService.deleteSignature();
        setSignature(null);
        setSaved(false);
        toast.success('Signature deleted successfully');
      } catch (error) {
        console.error('Failed to delete signature:', error);
        // The signature service already extracts error.response.data, so we access error.error directly
        const errorMessage = error?.error || error?.message || 'Failed to delete signature';
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="card max-w-3xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Manage Your Signature</h2>
      
      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <p className="mt-2 text-gray-600">Loading...</p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <h3 className="font-medium text-gray-900 mb-2">Your Electronic Signature</h3>
            <p className="text-sm text-gray-600 mb-4">
              Your signature will be used when signing rent receipts. Draw your signature in the box below.
            </p>
            
            {saved && (
              <div className="mb-4">
                <p className="text-sm text-green-600 mb-2">
                  You have a saved signature. You can use it to sign receipts or create a new one.
                </p>
                <div className="bg-white border rounded-md p-4 mb-4">
                  <img src={signature} alt="Your signature" className="max-h-20 mx-auto" />
                </div>
              </div>
            )}
            
            <SignaturePad
              initialSignature={signature}
              onSave={handleSaveSignature}
              onClear={handleDeleteSignature}
              label={saved ? "Create a new signature" : "Create your signature"}
            />
          </div>
          
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h3 className="font-medium text-blue-900 mb-2">About Electronic Signatures</h3>
            <ul className="list-disc text-sm text-blue-700 ml-5 space-y-1">
              <li>Your signature is securely stored and only used when you choose to sign a receipt.</li>
              <li>Electronic signatures are legally binding in most jurisdictions for rent receipts.</li>
              <li>You can update your signature at any time from this page.</li>
              <li>A signed receipt cannot be modified after signing.</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignatureManager;